import { PropsWithChildren } from 'react';
import { useStore } from '@tanstack/react-form';
import { useFieldContext } from '~/util/hooks/useFormContext';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/primitives/tooltip';
import { CircleQuestionMark } from 'lucide-react';
import { cn } from '~/util';

type FieldWithErrorBaseProps = {
    labelNotes?: string;
    required?: boolean;
    showErrorsEvenIfUntouched?: boolean;
    wrapperClassname?: string;
    hideLabelAndErrors?: boolean;
};

type FieldWithLabel = FieldWithErrorBaseProps & {
    hideLabelAndErrors?: false | undefined;
    label: string;
};

type FieldWithoutLabel = FieldWithErrorBaseProps & {
    hideLabelAndErrors: true;
    label?: never;
};

export type FieldWithErrorProps = FieldWithLabel | FieldWithoutLabel;

export function FieldLabelWithError<T>({
    label,
    labelNotes,
    required,
    children,
    showErrorsEvenIfUntouched = false,
    hideLabelAndErrors = false,
    wrapperClassname,
}: PropsWithChildren<FieldWithErrorProps>) {
    const field = useFieldContext<T>();
    const meta = useStore(field.store, (state) => state.meta);
    const errors =
        !hideLabelAndErrors &&
        (showErrorsEvenIfUntouched || meta.isTouched) &&
        !meta.isValid
            ? meta.errors
            : null;

    return (
        <div className={cn('flex flex-col', wrapperClassname)}>
            {!hideLabelAndErrors && (
                <>
                    <label
                        className={cn(required && 'required')}
                        htmlFor={field.name}
                    >
                        {errors ? (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <p className="cursor-pointer text-red-500 inline-flex items-center">
                                        {label}

                                        <CircleQuestionMark className="cursor-pointer text-red-500 text-sm h-4" />
                                    </p>
                                </TooltipTrigger>
                                <TooltipContent
                                    align="start"
                                    side="top"
                                    className="bg-background-light border-red-500"
                                >
                                    <ul className=" list-disc">
                                        {errors.map((error, index) => (
                                            <li
                                                key={index}
                                                className="max-w-xs"
                                            >
                                                {typeof error === 'string'
                                                    ? error
                                                    : error?.message}
                                            </li>
                                        ))}
                                    </ul>
                                </TooltipContent>
                            </Tooltip>
                        ) : (
                            <>{label}</>
                        )}
                    </label>
                    {labelNotes && (
                        <p className="text-secondary-light p-0 m-0 wrap-break-word leading-snug">
                            {labelNotes}
                        </p>
                    )}
                </>
            )}

            {children}
        </div>
    );
}
